// As seções das Configurações (#880): quais existem, em que ordem e quem vê cada
// uma. Moram com o menu porque o trilho, o cabeçalho do celular e a casca leem a
// mesma lista; a página das Configurações só desenha a seção escolhida.

import type { AcaoDePermissao, Me, RecursoDePermissao } from '@cliqcrm/contracts';
import { podeNaSessao } from '@/shared/sessao/contexto';

export type SecaoDaAdministracao =
  | 'configuracao'
  | 'pessoas'
  | 'permissoes'
  | 'etiquetas'
  | 'mensagens-prontas'
  | 'etapas'
  | 'campos-personalizados'
  | 'seguranca'
  | 'conexoes'
  | 'funcionalidades'
  | 'operadoras'
  | 'etapas-de-exame'
  | 'descarte';

/**
 * As seções, exportadas — quem as DESENHA agora é o menu lateral.
 *
 * ── Por que a lista mora aqui, e não na casca ─────────────────────────────
 *
 * Porque o `apoio` de cada uma é o que a seção faz, e isso é conhecimento
 * desta tela. A casca sabe pintar um item de menu; ela não sabe que
 * "Conexões" trata de motores, canais e portais.
 *
 * A casca importa a lista e monta o submenu. Se um dia nascer uma seção nova,
 * ela entra aqui e aparece no menu sem ninguém tocar no trilho.
 */
export interface SecaoDaAdministracaoDescrita {
  readonly chave: SecaoDaAdministracao;
  readonly nome: string;
  readonly apoio: string;
  /**
   * A permissão que a rota da seção confere (#732). Ausente = todo mundo vê.
   * Presente = só quem a tem, já com os ajustes da pessoa: a seção não abre
   * para quem a API recusaria.
   */
  readonly permissao?: { readonly recurso: RecursoDePermissao; readonly acao: AcaoDePermissao };
  /**
   * Fora do menu, mas ainda alcançável por quem aponta direto para ela.
   *
   * Existe por causa de Segurança: saiu do menu a pedido (23/09/2026), e o
   * aviso de "conta sem segundo fator" continua levando até ela. Apagar a
   * seção deixaria o aviso apontando para uma tela vazia.
   */
  readonly foraDoMenu?: boolean;
  /** Só com esta funcionalidade da loja ligada na organização (a clínica, #811). */
  readonly funcionalidade?: string;
}

export const SECOES_DA_ADMINISTRACAO: readonly SecaoDaAdministracaoDescrita[] = [
  {
    chave: 'configuracao',
    nome: 'Configuração',
    apoio: 'Fuso, expediente e a regra que abre oportunidade',
    // `GET /v1/admin/settings`: a Atendente, a Responsável e a Gestora abriam a
    // seção para receber a recusa (#732).
    permissao: { recurso: 'configuracao', acao: 'ler' },
  },
  /*
   * Pessoas vem LOGO DEPOIS de configuração, e antes de etiquetas.
   *
   * A ordem do menu é a ordem em que se monta uma revenda: primeiro o fuso e o
   * expediente, depois quem trabalha nela. Etiqueta e etapa são vocabulário —
   * úteis quando já há gente para usá-las.
   */
  {
    chave: 'pessoas',
    nome: 'Pessoas',
    apoio: 'Quem trabalha na organização, e o que cada um alcança',
    // `GET /v1/admin/pessoas` (#732).
    permissao: { recurso: 'usuario', acao: 'ler' },
  },
  /*
   * Logo depois de Pessoas: é o passo seguinte de quem monta a equipe, o que
   * cada uma pode além ou aquém do papel (ADR-0034, #1190). Só para quem
   * gerencia permissões: o Administrador, o Super Admin e quem recebeu o toggle.
   */
  {
    chave: 'permissoes',
    nome: 'Permissões',
    apoio: 'O que cada pessoa pode, além ou aquém do papel',
    permissao: { recurso: 'permissao', acao: 'editar' },
  },
  {
    chave: 'etiquetas',
    nome: 'Etiquetas',
    apoio: 'O vocabulário que a equipe cola nas conversas',
    // `GET /v1/etiquetas`: o Suporte Cliqfy não lê conversa (#732).
    permissao: { recurso: 'conversa', acao: 'ler' },
  },
  /*
   * Ao lado de Etiquetas, e não perto de Conexões.
   *
   * As duas são vocabulário da revenda — o que a equipe cola na conversa e o
   * que ela escreve nela. Conexões é fiação; quem vem ajustar o que a revenda
   * DIZ procura as duas no mesmo lugar.
   */
  {
    chave: 'mensagens-prontas',
    nome: 'Mensagens prontas',
    apoio: 'O texto que a equipe repete todo dia',
    // A lista é de quem lê conversa (#1192): o Suporte Cliqfy abriria a seção
    // para receber a recusa da API.
    permissao: { recurso: 'conversa', acao: 'ler' },
  },
  {
    /*
     * Mora no submenu do Funil desde 28/10 ("Configurar etapas"), e não mais no
     * de Configurações: é lá que quem mexe no quadro procura. A seção continua
     * aqui porque a tela é a das Configurações (o cabeçalho, as ações).
     */
    chave: 'etapas',
    nome: 'Configurar etapas',
    apoio: 'As colunas do quadro, e a ordem delas',
    foraDoMenu: true,
  },
  /*
   * Logo depois das etapas: também é vocabulário da organização, o que ela
   * guarda de cada cliente e de cada negócio além dos campos de sempre
   * (#568, ADR-0023). Gestor e admin; quem atende só preenche.
   */
  {
    chave: 'campos-personalizados',
    nome: 'Campos personalizados',
    apoio: 'O que a organização guarda além dos campos de sempre',
    permissao: { recurso: 'campo_personalizado', acao: 'editar' },
  },
  {
    chave: 'seguranca',
    nome: 'Segurança',
    apoio: 'Segundo fator da sua conta',
    foraDoMenu: true,
  },
  {
    chave: 'conexoes',
    nome: 'Conexões',
    apoio: 'Integrações, canais e portais',
    permissao: { recurso: 'integracao', acao: 'ler' },
  },
  {
    chave: 'funcionalidades',
    nome: 'Funcionalidades',
    apoio: 'Canais, portais e recursos para adicionar à organização',
    // `GET /v1/admin/flags`, como a seção Configuração (#732).
    permissao: { recurso: 'configuracao', acao: 'ler' },
  },
  {
    // A clínica (#811): só quem edita a configuração, e só com ela ligada.
    chave: 'operadoras',
    nome: 'Operadoras de convênio',
    apoio: 'As operadoras que a recepção escolhe no convênio do paciente',
    permissao: { recurso: 'configuracao', acao: 'editar' },
    funcionalidade: 'clinica',
  },
  {
    // A clínica (#811): a lista de etapas de onde cada pedido de exame escolhe.
    chave: 'etapas-de-exame',
    nome: 'Etapas de exame',
    apoio: 'As etapas que um pedido de exame costuma ter, e a ordem delas',
    permissao: { recurso: 'configuracao', acao: 'editar' },
    funcionalidade: 'clinica',
  },
  /*
    "Fila de descarte" saiu da lista.

    Ela mostra evento que falhou e não foi tratado — a fila de quem OPERA a
    plataforma, não de quem atende cliente. Quem abre Configurações procurando
    ajustar a organização não tem o que fazer com ela, e uma seção que ninguém
    sabe interpretar acaba gerando chamado em vez de resolver.

    A seção continua existindo e desenhada: devolvê-la aqui é uma linha.
  */
];

/**
 * As seções que ESTA pessoa vê, pelas permissões do `/v1/me` (#732).
 *
 * Uma função, e não um filtro escrito na casca, porque o menu do trilho e o
 * cabeçalho do celular precisam da mesma resposta — e duas cópias da regra são
 * duas chances de uma delas ficar para trás.
 */
export function secoesVisiveisPara(
  me: Pick<Me, 'permissoes' | 'flags'> | null | undefined,
): readonly SecaoDaAdministracaoDescrita[] {
  return SECOES_DA_ADMINISTRACAO.filter(
    (secao) =>
      !secao.foraDoMenu &&
      (!secao.permissao || podeNaSessao(me, secao.permissao.recurso, secao.permissao.acao)) &&
      (!secao.funcionalidade || me?.flags[secao.funcionalidade] === true),
  );
}
